import { useState, type Dispatch, type FC, type SetStateAction } from "react";
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import bigLogo from '@/assets/big-logo.svg';
import { AlertCircle } from "lucide-react";
import { resetPasswordApi } from '@/services/resetPasswordSevice';

interface RecoveryPasswordProps {
  setTypeForm: Dispatch<SetStateAction<"login" | "recovery">>;
}

const RecoveryPassword: FC<RecoveryPasswordProps> = ({ setTypeForm }) => {
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (error) setError(null);
    if (successMessage) setSuccessMessage(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    try {
      setLoading(true);
      const response: any = await resetPasswordApi.forgotPassword(
        formData.email
      );
      if (response?.success) {
        setSuccessMessage("Se ha enviado un enlace de recuperación, revisa tu bandeja de entrada.");
      } else {
        setError(
          response?.message ??
            "No se pudo procesar la solicitud. Inténtalo de nuevo más tarde."
        );
      }
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setError(err?.message || "Error desconocido");
      console.error("🚨 Error de login:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 flex items-center justify-center p-6 lg:p-8">
      <div className="w-full max-w-md">
        {/* Logo - Centrado y Grande */}
        <div className="mb-8 flex flex-col items-center">
          <img src={bigLogo} alt="SLYRSA Logo" className="h-20 w-auto mb-2" />
        </div>

        {/* Welcome Text */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Recuperar contraseña
          </h1>
          <p className="text-sm text-gray-600">Ingresa tu correo</p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-green-600"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  {successMessage}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && !successMessage && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
              <p className="ml-3 text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email Field */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-900 mb-2"
            >
              Correo electrónico
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-2.5 border-x-0 border-t-0 border-b-1 border-[#787A7D] rounded-none focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-0 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
              placeholder="correo@example.com"
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className={estiloDeBoton('primario', 'w-full')}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Cargando...
              </div>
            ) : (
              "Recuperar Contraseña"
            )}
          </button>
        </form>

        {/* Forgot Password Link */}
        <div className="mt-4 text-center">
          <p
            onClick={() => setTypeForm("login")}
            className="text-sm text-[#3B5998] hover:text-[#344e86] transition-colors cursor-pointer"
          >
            Ya tengo cuenta, iniciar sesión
          </p>
        </div>
      </div>
    </div>
  );
};

export default RecoveryPassword;
